import {
  IMG,
  ImageBand,
  PageHero,
  Reveal,
  Section,
  SectionHeader,
  FinalCta,
} from "../lib/shared"
import { PROCESS } from "../lib/data"
import { BentoCard, LaunchVisual, StatusVisual, TalkVisual } from "../lib/visuals"
import { Seo, breadcrumbSchema } from "../lib/seo"

const PROMISES = [
  {
    visual: <StatusVisual />,
    tone: "ink",
    t: "You'll always know where things stand",
    d: "A clear scope, a clear timeline and regular updates. You won't have to chase us for news.",
  },
  {
    visual: <TalkVisual />,
    tone: "accent",
    t: "You'll talk to the people doing the work",
    d: "Senior people from start to finish, with direct communication and no account managers in between.",
  },
  {
    visual: <LaunchVisual />,
    tone: "white",
    t: "You'll launch something that performs",
    d: "We measure success by what changes for your organisation, not by how the work looks in a portfolio.",
  },
] as const

export default function Process() {
  return (
    <>
      <Seo
        title="Our process"
        description="Discover, define, design, build, launch and support. The six stages Sableboxx uses to take a project from first conversation to working software."
        jsonLd={[breadcrumbSchema([{ name: "Process", path: "/process" }])]}
      />
      <PageHero
        eyebrow="How we work"
        title="From first requirements to digital systems you can rely on"
        intro="A calm, clear process that turns uncertainty into a plan, and a plan into something that works. You'll know what's happening at every stage."
        image={IMG.processHero}
      />

      {/* The six stages */}
      <Section tone="paper">
        <SectionHeader
          eyebrow="The six stages"
          title="What happens, and when"
          intro="Every project moves through the same six stages. A focused automation takes weeks and a platform takes months, but nothing gets skipped."
        />
        <div className="space-y-4">
          {PROCESS.map((p, i) => (
            <Reveal key={p.n} delay={(i % 2) * 60}>
              <div className="group grid grid-cols-1 items-center gap-4 rounded-2xl border border-neutral-200 bg-white p-8 transition-all duration-500 hover:border-transparent hover:bg-ink hover:text-white lg:grid-cols-12 lg:gap-10">
                <div className="flex items-center gap-6 lg:col-span-4">
                  <span className="text-[clamp(2.4rem,4vw,3.4rem)] font-black leading-none tracking-tight text-neutral-200 transition-colors group-hover:text-accent-bright">
                    {p.n}
                  </span>
                  <h3 className="text-[24px] font-bold tracking-[-0.02em]">
                    {p.t}
                  </h3>
                </div>
                <p className="text-[16px] leading-[1.7] text-neutral-600 transition-colors group-hover:text-neutral-300 lg:col-span-5">
                  {p.d}
                </p>
                <p className="text-[15px] font-semibold text-ink transition-colors group-hover:text-white lg:col-span-3 lg:text-right">
                  {p.tag}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <ImageBand
        tone="paper"
        src={IMG.processBand}
        alt="Laptops and notes on a shared table during a working session"
        eyebrow="How it feels"
        caption="Short cycles, visible progress and no surprises at the end"
      />

      {/* What you can expect */}
      <Section tone="mist">
        <SectionHeader
          eyebrow="What you can expect"
          title="What working with us is like"
          intro="Three commitments we make on every project, whatever its size."
        />
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          {PROMISES.map((p, i) => (
            <Reveal key={p.t} delay={i * 90}>
              <BentoCard tone={p.tone} title={p.t} text={p.d} visual={p.visual} />
            </Reveal>
          ))}
        </div>
      </Section>

      <FinalCta />
    </>
  )
}
